import { Button, Icon, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  crmPackageMappings,
  type CrmPackageMapping,
} from "../../api/crm-package-mappings.service.js";
import { CrmPackageMappingCreateModal } from "../../components/CrmPackageMappingCreateModal/index.js";
import { PageHeader } from "../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../components/UnauthorizedView/index.js";
import { ROUTES } from "../../routes.js";
import styles from "./CrmPackageMappings.module.css";

function CrmPackageMappingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [_authCheckLoading, setAuthCheckLoading] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      try {
        await crmPackageMappings.getAll({ limit: 1, offset: 0 });
        setAuthCheckLoading(false);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to check auth");
        const status = (error as Error & { status?: number }).status;
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          setAuthError(error);
        }
        setAuthCheckLoading(false);
      }
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setIsCreateModalOpen(true);
    }
  }, [searchParams]);

  function handleOpenCreateModal() {
    setIsCreateModalOpen(true);
  }

  function handleCloseCreateModal() {
    setIsCreateModalOpen(false);
  }

  function handleCreateSuccess(mapping: CrmPackageMapping) {
    setIsCreateModalOpen(false);
    setToast({
      message: `Mapping for "${mapping.crmPackageId}" created successfully`,
      type: "success",
    });

    const isOnDetailPage = location.pathname !== ROUTES.fulfilment.crmPackageMappings;

    if (isOnDetailPage) {
      navigate(ROUTES.fulfilment.crmPackageMappingDetail(mapping.id));
    } else {
      setRefetchTrigger(prev => prev + 1);
    }
  }

  function handleCreateError(error: Error) {
    const errorWithDetails = error as Error & {
      details?: Array<{ path: (string | number)[]; message: string }>;
    };
    let message = `Failed to create mapping: ${error.message}`;

    if (errorWithDetails.details?.length) {
      const fieldErrors = errorWithDetails.details
        .map(d => `${d.path.join(".")}: ${d.message}`)
        .join(", ");
      message = `Failed to create mapping: ${fieldErrors}`;
    }

    setToast({
      message,
      type: "error",
    });
  }

  function handleCloseToast() {
    setToast(null);
  }

  function handleMutationSuccess() {
    setRefetchTrigger(prev => prev + 1);
  }

  function handleBackClick() {
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate(ROUTES.fulfilment.crmPackageMappings);
    }
  }

  const isDetailPage = location.pathname !== ROUTES.fulfilment.crmPackageMappings;

  if (authError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="CRM Package Mappings"
          subtitle="Map CRM package IDs to fulfilment product codes"
        >
          <div style={{ display: "flex", gap: "12px" }}>
            {isDetailPage && !isEditMode && (
              <Button variant="outlined" color="default" onClick={handleBackClick}>
                <Icon icon="arrow-left" size="small" />
                Back
              </Button>
            )}
            {!isEditMode && (
              <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
                Create Mapping
              </Button>
            )}
          </div>
        </PageHeader>
      </div>

      <Outlet
        context={{
          toast,
          setToast,
          refetchTrigger,
          onMutationSuccess: handleMutationSuccess,
          setIsEditMode,
        }}
      />

      <CrmPackageMappingCreateModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
        onError={handleCreateError}
        prefillCrmPackageId={searchParams.get("crmPackageId") || undefined}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
          duration={5000}
        />
      )}
    </div>
  );
}

export default CrmPackageMappingsLayout;
